/**
 * SlotConflictAlert — the amber "that time is already taken" box shared by the
 * callback card's book and edit forms and the slot picker's footer.
 *
 * Lists what the chosen time collides with ("Maria already has: Callback —
 * Jane Doe · 10:00–10:30 AM") with an Override button (one press, no note:
 * owner's word) and, in the forms, a "Find another time" link that opens the
 * picker. Once overridden it shrinks to a small note that stays visible, so the
 * recruiter can see the booking will double up.
 */
import { AlertTriangle } from 'lucide-react';
import { Conflict, conflictHeadline, conflictLine } from './slotLogic';

interface Props {
  conflicts: Conflict[];
  overridden: boolean;
  /** The recruiter's display name (the headline). */
  name?: string | null;
  /** The recruiter is the viewer ("You already have:"). */
  isMe?: boolean;
  onOverride: () => void;
  /** Absent = no "Find another time" link (the picker's own footer). */
  onFindAnother?: () => void;
}

export default function SlotConflictAlert({ conflicts, overridden, name, isMe = false, onOverride, onFindAnother }: Props) {
  if (conflicts.length === 0 && !overridden) return null;
  if (overridden) {
    return (
      <p
        data-testid="slot-overridden"
        className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-400"
      >
        <AlertTriangle className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
        Overbooking — overridden
      </p>
    );
  }
  return (
    <div
      role="alert"
      data-testid="slot-conflict-alert"
      className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
    >
      <p className="flex items-start gap-1.5 font-medium">
        <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        {conflictHeadline(name, isMe)}
      </p>
      <ul className="mt-1 space-y-0.5 pl-5">
        {conflicts.map((c, i) => (
          <li key={`${c.kind}-${c.callback_id || c.start || ''}-${i}`} className="list-disc">
            {conflictLine(c)}
          </li>
        ))}
      </ul>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onOverride}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-900 sm:min-h-0"
        >
          Override
        </button>
        {onFindAnother && (
          <button
            type="button"
            onClick={onFindAnother}
            className="inline-flex min-h-[44px] items-center text-xs font-medium text-amber-800 underline underline-offset-2 transition-colors duration-150 hover:no-underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-amber-300 sm:min-h-0"
          >
            Find another time
          </button>
        )}
      </div>
    </div>
  );
}
